import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatRM, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Landmark, Loader2, CalendarDays, Tag, CreditCard } from "lucide-react";

export default function Checkout() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const { items, productItems, eventItems, total, productSubtotal, eventsSubtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [payCfg, setPayCfg] = useState({ enabled: false });
  const [payMethod, setPayMethod] = useState("bank_in");
  const [loading, setLoading] = useState(false);
  const [pointsToUse, setPointsToUse] = useState(0);
  const [form, setForm] = useState({
    shipping_name: user?.name || "",
    shipping_phone: user?.phone || "",
    shipping_address: "",
    notes: "",
  });

  useEffect(() => {
    if (!user) {
      navigate("/login?next=/checkout");
      return;
    }
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/payments/config").then((r) => setPayCfg(r.data)).catch(() => {});
  }, [user, navigate]);

  useEffect(() => {
    setForm((f) => ({
      ...f,
      shipping_name: f.shipping_name || user?.name || "",
      shipping_phone: f.shipping_phone || user?.phone || "",
    }));
  }, [user]);

  if (!user) return null;
  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-6 lg:px-10 py-16 text-center">
        <div className="font-serif-cn text-2xl text-[#003645]">{t("cart.empty")}</div>
        <Link to="/products" className="mt-6 inline-block text-[#005870] font-medium hover:text-[#00868e]">
          {t("cart.continueShopping")}
        </Link>
      </div>
    );
  }

  const availablePoints = Number(user?.points_balance || 0);
  // Points can only offset the PRODUCT subtotal — not event fees
  const maxPoints = Math.min(availablePoints, productSubtotal);
  const safePoints = Math.max(0, Math.min(Number(pointsToUse) || 0, maxPoints));
  const cashToPay = Math.max(0, total - safePoints);
  const requiresShipping = productItems.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (requiresShipping && (!form.shipping_name || !form.shipping_phone || !form.shipping_address)) {
      toast.error(zh ? "请完善收货信息" : "Please fill shipping info");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: productItems.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        events: eventItems.map((i) => ({
          event_id: i.event_id,
          quantity: i.quantity,
          name: form.shipping_name || user.name,
          phone: form.shipping_phone || user.phone,
        })),
        ...form,
        // For event-only orders, backend still expects strings — supply user's info
        shipping_name: form.shipping_name || user.name,
        shipping_phone: form.shipping_phone || user.phone || "-",
        shipping_address: form.shipping_address || (requiresShipping ? "" : "(event only)"),
        points_to_use: safePoints,
      };
      const { data } = await api.post("/orders", payload);
      clearCart();
      if (payMethod === "stripe" && payCfg.enabled) {
        try {
          const { data: sess } = await api.post("/payments/checkout", {
            kind: "order",
            ref_id: data.id,
            origin_url: window.location.origin,
          });
          window.location.href = sess.checkout_url;
          return;
        } catch (err) {
          toast.error(formatApiError(err));
          navigate(`/orders/${data.id}`);
          return;
        }
      }
      navigate(`/order-success/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16" data-testid="checkout-page">
      <h1 className="font-serif-cn text-4xl text-[#003645] font-semibold tracking-tight">
        {t("checkout.title")}
      </h1>

      <form onSubmit={submit} className="mt-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          {requiresShipping && (
            <div className="rounded-lg border border-[#e5ebeb] bg-white p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
                shipping
              </div>
              <h2 className="mt-2 font-serif-cn text-xl text-[#003645] font-semibold">
                {t("checkout.shipping")}
              </h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[#4a6e77] mb-1.5">
                    {t("checkout.shippingName")}
                  </label>
                  <input
                    className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200"
                    value={form.shipping_name}
                    onChange={(e) => setForm({ ...form, shipping_name: e.target.value })}
                    data-testid="shipping-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#4a6e77] mb-1.5">
                    {t("checkout.shippingPhone")}
                  </label>
                  <input
                    className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200"
                    value={form.shipping_phone}
                    onChange={(e) => setForm({ ...form, shipping_phone: e.target.value })}
                    data-testid="shipping-phone-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-[#4a6e77] mb-1.5">
                    {t("checkout.shippingAddress")}
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200"
                    value={form.shipping_address}
                    onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                    data-testid="shipping-address-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-[#4a6e77] mb-1.5">
                    {t("checkout.notes")}
                  </label>
                  <textarea
                    rows={2}
                    className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="shipping-notes-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Payment method selector */}
          <div className="rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="payment-method">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
              {zh ? "选择支付方式" : "Payment method"}
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setPayMethod("bank_in")}
                className={`text-left rounded-md border p-4 transition-colors duration-200 ${payMethod === "bank_in" ? "border-[#005870] bg-[#005870]/5" : "border-[#e5ebeb] bg-white hover:border-[#00868e]"}`}
                data-testid="pay-method-bank_in"
              >
                <div className="flex items-center gap-2 text-[#003645] font-medium">
                  <Landmark size={16} /> {zh ? "银行转账" : "Bank transfer"}
                </div>
                <div className="text-xs text-[#4a6e77] mt-1">{zh ? "转账后上传凭证，管理员审核" : "Transfer + upload proof, admin reviews"}</div>
              </button>
              <button
                type="button"
                disabled={!payCfg.enabled}
                onClick={() => setPayMethod("stripe")}
                className={`text-left rounded-md border p-4 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${payMethod === "stripe" ? "border-[#005870] bg-[#005870]/5" : "border-[#e5ebeb] bg-white hover:border-[#00868e]"}`}
                data-testid="pay-method-stripe"
              >
                <div className="flex items-center gap-2 text-[#003645] font-medium">
                  <CreditCard size={16} /> {zh ? "在线支付" : "Pay online"} <span className="text-[10px] text-[#4a6e77] font-normal">Stripe</span>
                </div>
                <div className="text-xs text-[#4a6e77] mt-1">{zh ? "信用卡支付，即时确认订单" : "Card payment, instant confirmation"}</div>
              </button>
            </div>
          </div>

          {/* Bank info (only when bank_in) */}
          {payMethod === "bank_in" && (
          <div className="rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="bank-info">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#005870]/10 text-[#005870] flex items-center justify-center">
                <Landmark size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
                  bank transfer
                </div>
                <h2 className="mt-1 font-serif-cn text-xl text-[#003645] font-semibold">
                  {t("checkout.bankInfo")}
                </h2>
              </div>
            </div>
            <dl className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
              <div>
                <dt className="text-[#4a6e77]">{t("checkout.bankName")}</dt>
                <dd className="mt-1 font-medium text-[#003645]" data-testid="bank-name">{settings.bank_name || "-"}</dd>
              </div>
              <div>
                <dt className="text-[#4a6e77]">{t("checkout.accountName")}</dt>
                <dd className="mt-1 font-medium text-[#003645]" data-testid="bank-account-name">{settings.bank_account_name || "-"}</dd>
              </div>
              <div>
                <dt className="text-[#4a6e77]">{t("checkout.accountNumber")}</dt>
                <dd className="mt-1 font-medium text-[#003645] tracking-wider" data-testid="bank-account-number">
                  {settings.bank_account_number || "-"}
                </dd>
              </div>
            </dl>
            <p className="mt-5 text-xs text-[#4a6e77] leading-relaxed">
              {t("checkout.afterOrder")}
            </p>
          </div>
          )}
        </div>

        {/* Summary */}
        <div>
          <div className="rounded-lg border border-[#e5ebeb] bg-white p-6 sticky top-24">
            <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
              summary
            </div>
            <div className="mt-4 space-y-2 text-sm">
              {items.map((it) => {
                const isEvent = it.type === "event";
                const name = zh ? it.name_zh : it.name_en;
                return (
                  <div key={isEvent ? `e-${it.event_id}` : `p-${it.product_id}`} className="flex justify-between gap-2">
                    <span className="text-[#4a6e77] truncate inline-flex items-center gap-1.5">
                      {isEvent ? <CalendarDays size={11} className="text-[#00868e]" /> : <Tag size={11} className="text-[#4a6e77]" />}
                      {name} × {it.quantity}
                    </span>
                    <span className="text-[#003645] shrink-0">
                      {formatRM(it.unit_price * it.quantity)}
                    </span>
                  </div>
                );
              })}
            </div>

            {productSubtotal > 0 && eventsSubtotal > 0 && (
              <div className="mt-4 pt-4 border-t border-[#e5ebeb] text-xs text-[#4a6e77] space-y-1">
                <div className="flex justify-between"><span>{t("cartTypes.product")}</span><span>{formatRM(productSubtotal)}</span></div>
                <div className="flex justify-between"><span>{t("cartTypes.event")}</span><span>{formatRM(eventsSubtotal)}</span></div>
              </div>
            )}

            {availablePoints > 0 && productSubtotal > 0 && (
              <div className="mt-5 pt-5 border-t border-[#e5ebeb]" data-testid="points-section">
                <label className="block text-xs text-[#4a6e77] mb-1.5">
                  {t("points.use")} · {t("points.max")} {formatRM(maxPoints)}
                </label>
                <input
                  type="number"
                  min="0"
                  max={maxPoints}
                  step="0.01"
                  className="w-full px-3 py-2 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white text-sm"
                  value={pointsToUse}
                  onChange={(e) => setPointsToUse(e.target.value)}
                  onBlur={(e) => {
                    const v = Math.max(0, Math.min(Number(e.target.value) || 0, maxPoints));
                    setPointsToUse(v.toFixed(2));
                  }}
                  data-testid="points-to-use-input"
                />
                <div className="mt-1 text-[11px] text-[#4a6e77]">{t("points.hint")}</div>
                {safePoints > 0 && (
                  <div className="mt-2 flex justify-between text-sm text-[#00868e]">
                    <span>- {t("points.title")}</span>
                    <span>- {formatRM(safePoints)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="mt-5 pt-5 border-t border-[#e5ebeb] flex justify-between font-semibold text-[#003645]">
              <span>{t("checkout.amount")}</span>
              <span className="text-[#005870] text-lg" data-testid="checkout-total">{formatRM(cashToPay)}</span>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200 disabled:opacity-60"
              data-testid="place-order-btn"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {payMethod === "stripe" && payCfg.enabled ? (zh ? "前往支付" : "Proceed to pay") : t("checkout.placeOrder")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
